import type { StateAction } from "@entrilia/shared/types";
import {
  arraysHaveSameItems,
  combineComparers,
  except,
  stringComparerBy,
} from "@entrilia/shared/utilities/arrayHelper";
import { combineValidators, maxCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";
import type {
  ClientUserGroupDetails,
  ClientUserGroupMember,
  CreateClientUserGroupRequest,
  UpdateClientUserGroupRequest,
} from "../../../../api/types/userManagementTypes";

export interface UserGroupDetailsState {
  name: string;
  members: ClientUserGroupMember[];
  validationResult: {
    isValid: boolean;
    errors: {
      name?: string;
    };
  };
}

export const getInitialUserGroupDetailsState = (): UserGroupDetailsState => ({
  name: "",
  members: [],
  validationResult: {
    isValid: false,
    errors: {},
  },
});

const validateName = combineValidators(requiredValidator, maxCharactersValidator(100));

const validateUserGroupDetailsState = (state: UserGroupDetailsState) => {
  const nameResult = validateName(state.name);
  return {
    isValid: nameResult.isValid,
    errors: { name: nameResult.error },
  };
};

export const isUserGroupDetailsStateDirty = (state: UserGroupDetailsState, storedUserGroup: ClientUserGroupDetails) => {
  return (
    state.name.trim() !== storedUserGroup.name.trim() ||
    !arraysHaveSameItems(
      state.members.map((m) => m.id),
      storedUserGroup.members.map((m) => m.id)
    )
  );
};

export const getCreateClientUserGroupRequest = (state: UserGroupDetailsState): CreateClientUserGroupRequest => {
  return {
    name: state.name.trim(),
    memberUserIds: state.members.map((m) => m.id),
  };
};

export const getUpdateClientUserGroupRequest = (
  state: UserGroupDetailsState,
  storedUserGroup: ClientUserGroupDetails
): UpdateClientUserGroupRequest => {
  const memberIds = state.members.map((m) => m.id);
  const storedMemberIds = storedUserGroup.members.map((m) => m.id);

  return {
    name: state.name.trim(),
    memberUserIdsToAdd: except(memberIds, storedMemberIds),
    memberUserIdsToRemove: except(storedMemberIds, memberIds),
  };
};

export const filterAndSortMembers = (members: ClientUserGroupMember[], searchTerm: string) => {
  const filtered = searchTerm
    ? members.filter(
        (member) =>
          member.name.toLocaleLowerCase().includes(searchTerm) || member.email.toLocaleLowerCase().includes(searchTerm)
      )
    : [...members];

  return filtered.sort(
    combineComparers(
      stringComparerBy((m) => m.name),
      stringComparerBy((m) => m.email)
    )
  );
};

// Actions

export const updateWithStoredUserGroupAction =
  (storedUserGroup: ClientUserGroupDetails): StateAction<UserGroupDetailsState> =>
  (state) => ({
    ...state,
    name: storedUserGroup.name,
    members: [...storedUserGroup.members],
    validationResult: {
      isValid: true,
      errors: {},
    },
  });

export const updateNameAction =
  (name: string): StateAction<UserGroupDetailsState> =>
  (state) => ({
    ...state,
    name,
    validationResult: validateUserGroupDetailsState({ ...state, name }),
  });

export const addMembersAction =
  (members: ClientUserGroupMember[]): StateAction<UserGroupDetailsState> =>
  (state) => ({
    ...state,
    members: [...state.members, ...members],
  });

export const removeMembersAction =
  (memberIds: string[]): StateAction<UserGroupDetailsState> =>
  (state) => ({
    ...state,
    members: state.members.filter((m) => !memberIds.includes(m.id)),
  });
